import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  approveSalaryChange,
  fetchSalaryChangeRequests,
  rejectSalaryChange,
} from '../../api/staff';
import { useToast } from '../../context/ToastContext';
import { getFriendlyError } from '../../utils/friendlyError';

const formatLkr = (n) => `LKR ${Number(n || 0).toLocaleString()}`;

const SalaryApprovalsPanel = () => {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['salary-change-requests'],
    queryFn: fetchSalaryChangeRequests,
    staleTime: 30_000,
  });

  const requests = data?.requests || [];

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['salary-change-requests'] });
    queryClient.invalidateQueries({ queryKey: ['staff-salary'] });
    queryClient.invalidateQueries({ queryKey: ['staff-performance'] });
  };

  const approveMut = useMutation({
    mutationFn: approveSalaryChange,
    onSuccess: () => {
      showToast(t('staff.salary.approved'), 'success');
      invalidate();
    },
    onError: (err) => showToast(getFriendlyError(err, t('staff.salary.approveFailed')), 'error'),
  });

  const rejectMut = useMutation({
    mutationFn: rejectSalaryChange,
    onSuccess: () => {
      showToast(t('staff.salary.rejected'), 'success');
      invalidate();
    },
    onError: (err) => showToast(getFriendlyError(err, t('staff.salary.rejectFailed')), 'error'),
  });

  if (isLoading) return null;
  if (!requests.length) return null;

  const busy = approveMut.isPending || rejectMut.isPending;

  return (
    <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-4">
      <h3 className="text-sm font-medium">{t('staff.salary.approvalsTitle')}</h3>
      <p className="mt-1 text-xs text-zinc-500">{t('staff.salary.approvalsHint')}</p>
      <div className="mt-4 space-y-3">
        {requests.map((req) => (
          <div
            key={req._id}
            className="rounded-lg border border-zinc-200 bg-white p-3 dark:border-zinc-800 dark:bg-zinc-900"
          >
            <p className="font-medium">{req.staffName}</p>
            <p className="mt-1 text-xs text-zinc-500">
              {t('staff.salary.requestedBy', { name: req.requestedByName })}
            </p>
            <div className="mt-2 grid gap-2 text-xs sm:grid-cols-2">
              <div>
                <p className="text-zinc-500">{t('staff.salary.current')}</p>
                <p>
                  {formatLkr(req.previousSalary.dailyAmount)} / {formatLkr(req.previousSalary.weeklyAmount)} / {formatLkr(req.previousSalary.monthlyAmount)}
                </p>
              </div>
              <div>
                <p className="text-zinc-500">{t('staff.salary.proposed')}</p>
                <p className="text-glow-400">
                  {formatLkr(req.proposedSalary.dailyAmount)} / {formatLkr(req.proposedSalary.weeklyAmount)} / {formatLkr(req.proposedSalary.monthlyAmount)}
                </p>
              </div>
            </div>
            <div className="mt-3 flex gap-2">
              <button
                type="button"
                disabled={busy}
                onClick={() => approveMut.mutate(req._id)}
                className="flex-1 rounded-lg bg-emerald-600 py-1.5 text-xs text-white disabled:opacity-50"
              >
                {t('staff.salary.approve')}
              </button>
              <button
                type="button"
                disabled={busy}
                onClick={() => rejectMut.mutate(req._id)}
                className="flex-1 rounded-lg border border-zinc-300 py-1.5 text-xs dark:border-zinc-700 disabled:opacity-50"
              >
                {t('staff.salary.reject')}
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default SalaryApprovalsPanel;
